import { resetWithoutConfirm } from 'actions'
import * as graphActions from 'actions/graph'
import * as nodeActions from 'actions/nodes'
import * as edgeActions from 'actions/edges'
import * as kindActions from 'actions/kinds'
import direction from 'constants/direction'
import selectFocus from 'selectors/graph/focus'
import { selectNeighborhood } from 'selectors/graph/neighborhood'
import selectPinned from 'selectors/graph/pinned'
import * as selectKinds from 'selectors/graph/kinds'

const EMBED_READY_EVENT = '__ready'
const REGISTER_MESSAGE_TYPE = '__register'
const VISIBLE_GRAPH_CHANGED_EVENT = 'visibleGraphChanged'
const BACKGROUND_TOUCHED_EVENT = 'backgroundTouched'

type StoreLike = {
  dispatch: (action: unknown) => unknown
  getState: () => unknown
  subscribe: (listener: () => void) => () => void
}

type MessageRecord = Record<string, unknown>
type MessageHandler = (payload: MessageRecord) => Promise<Record<string, unknown> | void>

const isRecord = (value: unknown): value is MessageRecord => {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

const asNonEmptyString = (value: unknown, field: string): string => {
  if (typeof value !== 'string' || value.trim() === '') {
    throw new Error(`${field} must be a non-empty string`)
  }
  return value
}

const asOptionalString = (value: unknown, field: string): string | undefined => {
  if (value == null) {
    return undefined
  }
  if (typeof value !== 'string') {
    throw new Error(`${field} must be a string`)
  }
  return value
}

const asOptionalNumber = (value: unknown, field: string): number | undefined => {
  if (value == null) {
    return undefined
  }
  if (typeof value !== 'number' || Number.isNaN(value)) {
    throw new Error(`${field} must be a number`)
  }
  return value
}

const asOptionalBoolean = (value: unknown, field: string): boolean | undefined => {
  if (value == null) {
    return undefined
  }
  if (typeof value !== 'boolean') {
    throw new Error(`${field} must be a boolean`)
  }
  return value
}

const EDGE_DIRECTION_VALUES = ['free', 'ltr', 'rtl', 'ttb', 'btt'] as const

const asOptionalEdgeDirection = (value: unknown, field: string): typeof EDGE_DIRECTION_VALUES[number] | undefined => {
  if (value == null) {
    return undefined
  }
  if (typeof value !== 'string' || !(EDGE_DIRECTION_VALUES as readonly string[]).includes(value)) {
    throw new Error(`${field} must be one of ${EDGE_DIRECTION_VALUES.join(', ')}`)
  }
  return value as typeof EDGE_DIRECTION_VALUES[number]
}

type EdgeKindStylePatch = {
  color?: string
  edgeVisible?: boolean
  edgeSpring?: boolean
  edgeStiffness?: number
  edgeSpringLength?: number
  edgeDirection?: typeof EDGE_DIRECTION_VALUES[number]
  edgeDirectionSoft?: boolean
  edgeThickness?: number
}

const asKindStylePatch = (payload: MessageRecord): EdgeKindStylePatch => {
  const color = asOptionalString(payload.color, 'color')
  const edgeVisible = asOptionalBoolean(payload.edgeVisible, 'edgeVisible')
  const edgeSpring = asOptionalBoolean(payload.edgeSpring, 'edgeSpring')
  const edgeStiffness = asOptionalNumber(payload.edgeStiffness, 'edgeStiffness')
  const edgeSpringLength = asOptionalNumber(payload.edgeSpringLength, 'edgeSpringLength')
  const edgeDirection = asOptionalEdgeDirection(payload.edgeDirection, 'edgeDirection')
  const edgeDirectionSoft = asOptionalBoolean(payload.edgeDirectionSoft, 'edgeDirectionSoft')
  const edgeThickness = asOptionalNumber(payload.edgeThickness, 'edgeThickness')
  return {
    ...(color !== undefined ? { color } : {}),
    ...(edgeVisible !== undefined ? { edgeVisible } : {}),
    ...(edgeSpring !== undefined ? { edgeSpring } : {}),
    ...(edgeStiffness !== undefined ? { edgeStiffness } : {}),
    ...(edgeSpringLength !== undefined ? { edgeSpringLength } : {}),
    ...(edgeDirection !== undefined ? { edgeDirection } : {}),
    ...(edgeDirectionSoft !== undefined ? { edgeDirectionSoft } : {}),
    ...(edgeThickness !== undefined ? { edgeThickness } : {}),
  }
}

const asStringArray = (value: unknown, field: string): string[] => {
  if (!Array.isArray(value)) {
    throw new Error(`${field} must be an array`)
  }
  const normalized = value.map((item) => {
    if (typeof item !== 'string' || item.trim() === '') {
      throw new Error(`${field} must contain non-empty strings`)
    }
    return item
  })
  return Array.from(new Set(normalized))
}

const normalizeDirectionality = (value: unknown): string | undefined => {
  if (value == null) {
    return undefined
  }
  if (typeof value !== 'string') {
    throw new Error('direction must be a string when provided')
  }
  const normalized = value.trim().toUpperCase()
  if (normalized === direction.DIRECTED) {
    return direction.DIRECTED
  }
  if (normalized === direction.REVERSE) {
    return direction.REVERSE
  }
  if (normalized === direction.BIDIRECTIONAL) {
    return direction.BIDIRECTIONAL
  }
  if (normalized === direction.UNDIRECTED) {
    return direction.UNDIRECTED
  }
  throw new Error('direction must be one of DIRECTED, REVERSE, BIDIRECTIONAL, UNDIRECTED')
}

const asPayload = (args: unknown): MessageRecord => {
  if (!Array.isArray(args) || args.length === 0) {
    return {}
  }
  const first = args[0]
  if (!isRecord(first)) {
    throw new Error('args[0] must be an object')
  }
  return first
}

const safePostMessage = (
  target: MessageEventSource | null,
  payload: Record<string, unknown>,
  targetOrigin: string
) => {
  if (!target || typeof (target as Window).postMessage !== 'function') {
    return
  }
  const normalizedTargetOrigin =
    targetOrigin && targetOrigin !== 'null' ? targetOrigin : '*'
  ;(target as Window).postMessage(payload, normalizedTargetOrigin)
}

type VisibleGraphPayload = {
  nodes: Record<string, { content?: string; kind?: string }>
  edges: Record<string, { content?: string; kind?: string; from?: string; to?: string; directionality?: string }>
  kinds: Record<string, EdgeKindStylePatch>
  focus: string
  pinned: string[]
}

const getVisibleGraphPayload = (state: unknown): VisibleGraphPayload => {
  const typedState = state as Parameters<typeof selectNeighborhood>[0]
  const neighborhood = selectNeighborhood(typedState)
  const kinds = selectKinds.all(typedState as Record<string, unknown>)
  const focus = selectFocus(typedState as Parameters<typeof selectFocus>[0])
  const pinnedState = selectPinned(typedState as Record<string, unknown>)

  const simplifiedNodes = Object.keys(neighborhood.nodes)
    .sort()
    .reduce<Record<string, { content?: string; kind?: string }>>((acc, id) => {
      const node = neighborhood.nodes[id]
      const next: { content?: string; kind?: string } = {}
      if (node?.data?.content !== undefined) {
        next.content = node.data.content
      }
      if (node?.data?.kind !== undefined) {
        next.kind = node.data.kind
      }
      acc[id] = next
      return acc
    }, {})

  const edges = Object.keys(neighborhood.edges)
    .sort()
    .reduce<Record<string, { content?: string; kind?: string; from?: string; to?: string; directionality?: string }>>((acc, id) => {
      const edge = neighborhood.edges[id]
      const next: { content?: string; kind?: string; from?: string; to?: string; directionality?: string } = {}
      if (edge?.data?.content !== undefined) {
        next.content = edge.data.content
      }
      if (edge?.data?.kind !== undefined) {
        next.kind = edge.data.kind
      }
      if (edge?.data?.from !== undefined) {
        next.from = edge.data.from
      }
      if (edge?.data?.to !== undefined) {
        next.to = edge.data.to
      }
      if (edge?.data?.directionality !== undefined) {
        next.directionality = edge.data.directionality
      }
      acc[id] = next
      return acc
    }, {})

  const simplifiedKinds = Object.keys(kinds)
    .sort()
    .reduce<Record<string, EdgeKindStylePatch>>((acc, id) => {
      const styles = kinds[id]?.data?.styles
      acc[id] = styles ? asKindStylePatch(styles as MessageRecord) : {}
      return acc
    }, {})

  const pinned = Object.keys(pinnedState.nodes)
    .filter((id) => simplifiedNodes[id] != null)
    .sort()

  return {
    nodes: simplifiedNodes,
    edges,
    kinds: simplifiedKinds,
    focus: focus.id,
    pinned,
  }
}

const buildVisibleGraphSignature = (payload: VisibleGraphPayload) => JSON.stringify(payload)

const getPointerData = (event: Event) => {
  if (event instanceof PointerEvent || event instanceof MouseEvent) {
    return {
      clientX: event.clientX,
      clientY: event.clientY,
      pageX: event.pageX,
      pageY: event.pageY,
    }
  }
  if (event instanceof TouchEvent) {
    const touch = event.changedTouches[0] || event.touches[0]
    if (!touch) {
      return null
    }
    return {
      clientX: touch.clientX,
      clientY: touch.clientY,
      pageX: touch.pageX,
      pageY: touch.pageY,
    }
  }
  return null
}

export const setupEmbedMessageHandler = (store: StoreLike) => {
  let registeredSecret: string | null = null
  let visibleGraphSignature: string | null = null

  const emitSdkEvent = (eventName: string, payload: Record<string, unknown>) => {
    if (window.parent === window || registeredSecret == null) {
      return
    }
    safePostMessage(window.parent, { event: eventName, payload }, '*')
  }

  const emitVisibleGraphIfChanged = (force = false) => {
    const payload = getVisibleGraphPayload(store.getState())
    const signature = buildVisibleGraphSignature(payload)
    if (!force && signature === visibleGraphSignature) {
      return
    }
    visibleGraphSignature = signature
    emitSdkEvent(VISIBLE_GRAPH_CHANGED_EVENT, payload)
  }

  const methods: Record<string, MessageHandler> = {
    async createNode(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      const content = asOptionalString(payload.content, 'content')
      const kind = asOptionalString(payload.kind, 'kind')
      await Promise.resolve(store.dispatch(nodeActions.createOneNode({
        id,
        ...(content !== undefined ? { content } : {}),
        ...(kind !== undefined ? { kind } : {}),
      })))
      return { id }
    },
    async createEdge(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      const from = asNonEmptyString(payload.from, 'from')
      const to = asNonEmptyString(payload.to, 'to')
      const content = asOptionalString(payload.content, 'content')
      const kind = asOptionalString(payload.kind, 'kind')
      const directionality = normalizeDirectionality(payload.directionality ?? payload.direction)
      await Promise.resolve(store.dispatch(edgeActions.createOneEdge({
        id,
        from,
        to,
        ...(content !== undefined ? { content } : {}),
        ...(kind !== undefined ? { kind } : {}),
        ...(directionality !== undefined ? { directionality } : {}),
      })))
      return { id }
    },
    async createKind(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      const color = asNonEmptyString(payload.color, 'color')
      const stylePatch = asKindStylePatch(payload)
      await Promise.resolve(store.dispatch(kindActions.createOneKind({
        id,
        label: id,
        styles: { ...stylePatch, color },
      })))
      return { id }
    },
    async editNode(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      const content = asOptionalString(payload.content, 'content')
      const kind = asOptionalString(payload.kind, 'kind')
      await Promise.resolve(store.dispatch(nodeActions.editOneNode({
        id,
        ...(content !== undefined ? { content } : {}),
        ...(kind !== undefined ? { kind } : {}),
      })))
      return { id }
    },
    async editEdge(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      const content = asOptionalString(payload.content, 'content')
      const kind = asOptionalString(payload.kind, 'kind')
      const directionValue = payload.directionality ?? payload.direction
      const directionality = normalizeDirectionality(directionValue)
      await Promise.resolve(store.dispatch(edgeActions.editOneEdge({
        id,
        ...(content !== undefined ? { content } : {}),
        ...(kind !== undefined ? { kind } : {}),
        ...(directionality !== undefined ? { directionality } : {}),
      })))
      return { id }
    },
    async editKind(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      const currentKind = selectKinds.selectOne(
        store.getState() as Record<string, unknown>,
        { params: { kindId: id } }
      )
      // Merge onto existing styles instead of replacing them, so editing one
      // field (e.g. color) doesn't wipe out previously configured edge styles.
      const nextStyles = { ...currentKind?.data?.styles, ...asKindStylePatch(payload) }
      await Promise.resolve(store.dispatch(kindActions.editOneKind({
        id,
        label: currentKind?.data?.label || id,
        styles: nextStyles,
      })))
      return { id }
    },
    async deleteNode(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      await Promise.resolve(store.dispatch(nodeActions.deleteOneNode({ id, autoConfirm: true })))
      return { id }
    },
    async deleteEdge(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      await Promise.resolve(store.dispatch(edgeActions.deleteOneEdge({ id, autoConfirm: true })))
      return { id }
    },
    async deleteKind(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      await Promise.resolve(store.dispatch(kindActions.deleteOneKind({ id })))
      return { id }
    },
    async clearGraph() {
      await Promise.resolve(store.dispatch(resetWithoutConfirm()))
      return { ok: true }
    },
    async focus(payload) {
      const id = asNonEmptyString(payload.id, 'id')
      await Promise.resolve(store.dispatch(graphActions.focusOne({ id })))
      return { id }
    },
    async pin(payload) {
      const ids = asStringArray(payload.ids, 'ids')
      await Promise.resolve(store.dispatch(nodeActions.pinMultiNode({ ids })))
      return { ids }
    },
    async unpin(payload) {
      const ids = asStringArray(payload.ids, 'ids')
      await Promise.resolve(store.dispatch(nodeActions.unpinMultiNode({ ids })))
      return { ids }
    },
  }

  window.addEventListener('message', async (event: MessageEvent<unknown>) => {
    if (event.source !== window.parent) {
      return
    }

    const data = event.data
    if (!isRecord(data)) {
      return
    }

    if (data.type === REGISTER_MESSAGE_TYPE) {
      if (registeredSecret == null) {
        const incomingSecret = data.secret
        if (typeof incomingSecret === 'string' && incomingSecret.trim() !== '') {
          registeredSecret = incomingSecret
          emitVisibleGraphIfChanged(true)
        }
      }
      return
    }

    const callId = data.callId
    if (typeof callId !== 'string' || callId.trim() === '') {
      return
    }

    const respond = (payload: Record<string, unknown>) => {
      safePostMessage(event.source, payload, event.origin)
    }

    if (registeredSecret == null) {
      respond({ callId, error: 'Embed client is not registered yet' })
      return
    }

    if (data.secret !== registeredSecret) {
      respond({ callId, error: 'Invalid embed secret' })
      return
    }

    const methodName = data.method
    if (typeof methodName !== 'string') {
      respond({ callId, error: 'method must be a string' })
      return
    }

    const handler = methods[methodName]
    if (!handler) {
      respond({ callId, error: `Unsupported method: ${methodName}` })
      return
    }

    try {
      const payload = asPayload(data.args)
      const result = await handler(payload)
      respond({ callId, result: result ?? { ok: true } })
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown embed error'
      respond({ callId, error: message })
    }
  })

  if (window.parent !== window) {
    store.subscribe(() => {
      emitVisibleGraphIfChanged()
    })

    const onBackgroundTouched = (event: Event) => {
      const target = event.target
      if (!(target instanceof Element)) {
        return
      }
      const inGraphSurface = target.closest('.graph, .graph-wrapper')
      if (!inGraphSurface) {
        return
      }

      const graphTypeElement = target.closest('[data-graph-type]')
      if (graphTypeElement) {
        return
      }

      if (target.closest('.graph-fade-overlay')) {
        return
      }

      const pointer = getPointerData(event)
      if (!pointer) {
        return
      }
      emitSdkEvent(BACKGROUND_TOUCHED_EVENT, pointer)
    }

    window.addEventListener('pointerdown', onBackgroundTouched, true)
    window.parent.postMessage({ event: EMBED_READY_EVENT }, '*')
  }
}
